import { flash } from "@xola/ui-kit";
import { getMeta, isMembershipItem, Loader } from "common";
import ejs from "ejs/ejs.min";
import { get } from "lodash";
import React, { useEffect } from "react";
import { useMutation } from "react-query";
import {
    CheckInVerificationStatus,
    ExperiencePurchaseItem,
    Guest,
    GuestStatus,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    SellerMeta,
    Unit,
    UnitType,
} from "types";
import { CheckInAndPrintTicketModal } from "../Common/CheckInAndPrintTicketModal";
import { api } from "@/api/api";
import { updateTimeslotCache } from "@/components/Event/EditEventCapacityModal";
import { isItemLevelCheckIn } from "@/components/PurchaseModification/CheckInPurchaseGuests/CheckInHelper";
import {
    getVerificationTagUpdateAction,
    useVerificationTags,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/useVerificationTags";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getArrivalDate, getArrivalTime } from "@/models/PurchaseItem";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { print, PrintType } from "@/utils/qzTray";

// Equipment without collect-demographics carries no attendee guests, so it check-ins/prints at the item
// level. We synthesize a single item-level pseudo-guest (id = item id) so the modal can render a row and
// generate one ticket for the item.
const createItemLevelGuest = (item: PurchaseItem): Guest =>
    ({
        id: item.id,
        name: item.name ?? "",
        guestStatus: (item.guestStatus as unknown as GuestStatus) ?? GuestStatus.PENDING,
        // No demographic unit exists; DemographicItem renders nothing for an unresolved unit id.
        unit: { id: "" } as Unit,
        primary: true,
        organizer: true,
    } as Guest);

export const getGuestsForPrintTicketModal = (
    purchase?: Purchase | null,
    isSellerCheckInPreferenceEnabled?: boolean,
) => {
    const experiences = useCreatePurchaseStore.getState().getProductsInPurchase();
    if (!purchase) {
        return [];
    }

    const guests: Guest[] = [];
    for (const item of purchase.items) {
        const purchaseItemExperience = experiences.find((experience) => experience.id === item.product.id);
        const isCheckInEnabled = purchaseItemExperience?.checkInPreference
            ? purchaseItemExperience?.checkInPreference?.enabled
            : isSellerCheckInPreferenceEnabled;
        if (!isCheckInEnabled || isMembershipItem(item)) {
            continue;
        }

        if (isItemLevelCheckIn(item as ExperiencePurchaseItem)) {
            guests.push(createItemLevelGuest(item));
        } else if (item.guests) {
            for (const guest of item.guests) {
                guests.push(guest);
            }
        }
    }

    return guests;
};

const getPurchaseItem = (purchase: Purchase, guest: Guest) => {
    // Item-level pseudo-guests use the item id as their id; otherwise match by membership in item.guests.
    const purchaseItem = purchase.items.find(
        (item) => item.id === guest.id || item.guests?.some((g) => g.id === guest.id),
    );
    return purchaseItem ?? null;
};

export const generateTickets = (guests: Guest[], purchase: Purchase, template: string, units?: Unit[]) => {
    const tickets: any = [];
    for (const guest of guests) {
        try {
            const purchaseItem = getPurchaseItem(purchase, guest);
            const isExperiencePurchaseItem =
                !!purchaseItem &&
                [PurchaseItemObject.EXPERIENCE, PurchaseItemObject.ORDER].includes(purchaseItem.object);

            const unit = units?.find((unit) => unit.id === guest.unit.id);

            // make the ticket data backward compatible so that templates don't need to be updated
            const data = {
                order: {
                    ...purchase,
                    experienceName: purchaseItem?.name ?? "",
                    arrival:
                        purchaseItem && isExperiencePurchaseItem
                            ? getArrivalDate(purchaseItem as ExperiencePurchaseItem)
                            : "",
                    arrivalTime:
                        purchaseItem && purchaseItem.object === PurchaseItemObject.EXPERIENCE
                            ? getArrivalTime(purchaseItem)
                            : "",
                },
                guest: {
                    ...guest,
                    demographic: unit
                        ? {
                              label: unit.name,
                              shortcode: unit.name.slice(0, 1).toUpperCase(),
                              caption: unit.description,
                          }
                        : {},
                },
            };
            const ticket = ejs.render(template, data);
            tickets.push(ticket);
        } catch (error) {
            console.log("Error rendering ticket", error);
            flash.danger("Error rendering ticket");
        }
    }

    return tickets;
};

const getCodes = (meta: SellerMeta) => {
    const paperCutCode = meta.features?.pos?.paperCutCode;
    const drawerEjectCode = meta.features?.pos?.drawerEjectCode;
    const codes: string[] = [];

    if (paperCutCode) {
        console.log("Paper cut code:", paperCutCode);
        codes.push(paperCutCode);
    }

    if (drawerEjectCode) {
        console.log("Drawer eject code:", drawerEjectCode);
        codes.push(drawerEjectCode);
    }

    return codes;
};

const getGroupedGuestsForPurchaseItem = (purchase: Purchase, guests: Guest[]) => {
    if (!purchase) {
        return {};
    }

    if (purchase) {
        const groups: any = {};
        for (const item of purchase.items) {
            groups[item.id] = {
                // Item-level pseudo-guests carry the item id; regular guests match by membership.
                guests: guests.filter((g) => g.id === item.id || item.guests?.some((guest) => guest.id === g.id)),
                purchaseItem: item,
            };
        }

        return groups;
    }
};

interface SaveGuestStatusPayload {
    guests: Guest[];
    purchaseItemId: ID;
}

interface CheckInAndPrintTicketProps {
    isOpen: boolean;
    onComplete: () => void;
    purchase: Purchase;
    /** If check-in modal is opened for a specified purchase item */
    purchaseItem?: ExperiencePurchaseItem;
    guests?: Guest[];
}

export const CheckInAndPrintTicket = ({
    isOpen,
    purchase,
    guests,
    purchaseItem,
    onComplete,
}: CheckInAndPrintTicketProps) => {
    const seller = useSeller();
    const meta = getMeta(seller);
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const { data: units, isLoading: isLoadingUnits } = useAutoFetchCollection<Unit>(
        "units",
        { type: UnitType.DEMOGRAPHIC },
        { enabled: isOpen },
    );

    const isSellerCheckInPreferenceEnabled = seller.preferences?.checkIn?.enabled;
    const guestsForPrintModal = guests ?? getGuestsForPrintTicketModal(purchase, isSellerCheckInPreferenceEnabled);

    const { mutateAsync: saveGuestStatus, isLoading } = useMutation(
        async ({ guests, purchaseItemId }: SaveGuestStatusPayload) => {
            // Item-level check-in (equipment without demographics): the group holds a single pseudo-guest whose
            // id is the item id. Apply status at the item level via items[].guestStatus. XS-1821.
            const isItemLevel = guests.length === 1 && guests[0].id === purchaseItemId;
            const itemPayload = isItemLevel
                ? { id: purchaseItemId, guestStatus: guests[0].guestStatus }
                : { id: purchaseItemId, guests };
            await api.patch(`/purchases/${purchase.id}`, { items: [itemPayload] });
        },
    );

    const { updateVerificationTags } = useVerificationTags(purchase, purchaseItem);

    const updateGuestStatus = async (guests: Guest[], verificationStatus?: CheckInVerificationStatus) => {
        const groups = getGroupedGuestsForPurchaseItem(purchase, guests);
        for (const key of Object.keys(groups)) {
            if (groups[key]?.guests?.length < 1) {
                continue;
            }

            const timeslotParams = {
                timeslot: null,
                event: null,
                productId: groups[key]?.purchaseItem.product.id,
                startDate: groups[key]?.purchaseItem.arrivalDate,
            };

            const updateParams = {
                isGranted: isGranted,
            };

            const updateTagAction = getVerificationTagUpdateAction(GuestStatus.ARRIVED, guests, verificationStatus);
            if (updateTagAction) {
                updateVerificationTags(updateTagAction, verificationStatus);
            }

            await saveGuestStatus({ guests: groups[key]?.guests, purchaseItemId: key });

            invalidate("purchases", purchase?.id);
            invalidate("purchaseItems", groups[key]?.purchaseItem.id);

            await updateTimeslotCache(timeslotParams, updateParams);

            if (groups[key]?.purchaseItem.event) {
                invalidate("events", groups[key]?.purchaseItem.event.id);
            }
        }
    };

    const onPrint = async (values: { guests: Guest[]; verificationStatus?: CheckInVerificationStatus }) => {
        const { guests, verificationStatus } = values;
        await updateGuestStatus(guests, verificationStatus);

        const template = get(seller, "preferences.ticket.template");
        if (template) {
            const guestsToPrint = guests
                .filter((guest) => guest.ticketPrintStatus?.printed)
                .map((guest) => {
                    const unit = units.find((unit) => unit.id === guest.unit.id);
                    if (unit) {
                        return { ...guest, unit: { ...unit, shortCode: unit.name.slice(0, 1).toUpperCase() } };
                    }

                    return guest;
                }) as Guest[];

            const tickets = generateTickets(guestsToPrint, purchase, template, units);
            for (const ticket of tickets) {
                await print(ticket, getCodes(meta), PrintType.TICKET);
            }
        }

        onComplete();
    };

    // This useEffect is only for avoiding CheckInAndPrintTicket if purchase has only gift items
    const shouldInvokeComplete = guestsForPrintModal.length === 0 && !isLoadingUnits;
    useEffect(() => {
        if (shouldInvokeComplete) {
            onComplete();
        }
    }, [shouldInvokeComplete, onComplete]);

    if (isLoadingUnits) {
        return <Loader />;
    }

    return guestsForPrintModal.length > 0 ? (
        <CheckInAndPrintTicketModal
            isOpen={isOpen}
            guests={guestsForPrintModal}
            purchaseItem={purchaseItem}
            isSaving={isLoading}
            onClose={onComplete}
            onPrint={onPrint}
        />
    ) : null;
};
